import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useDeleteOrganizationUser } from '@/modules/users/hooks/useOrganizationUsers';
import { OrganizationUserDto } from '@cargovibe/shared-types';
import { formatFullName } from '@/shared/services/formatFullName';

interface Props {
  user: OrganizationUserDto | null;
  onClose: () => void;
}

export function DeleteUserDialog({ user, onClose }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const deleteMutation: ReturnType<typeof useDeleteOrganizationUser> = useDeleteOrganizationUser();

  const handleDelete = (): void => {
    if (!user) return;
    deleteMutation.mutate(user.id, { onSuccess: onClose });
  };

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  const userName: string = user ? formatFullName(user.firstname, user.lastname) : '';

  return (
    <Dialog open={!!user} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('users.deleteUser')}</DialogTitle>
          <DialogDescription>
            {t('users.deleteUserConfirm')} <strong>{userName}</strong>{' '}
            {t('users.deleteUserDescription')}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={deleteMutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
